import { darken, useTheme } from '@material-ui/core';
import { ResponsiveLine } from '@nivo/line';
import _, { chain, orderBy } from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { useEffect } from 'react';
import AnalysisChartLoading from '../../AnalysisChartLoading';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';
import { ChartProps } from '../../AnalysisList';
import { internationalText } from '../../internationalText';
import DataTimedOut, { didQueryTimeout } from '../DataTimedOut';
import NotEnoughData from '../NotEnoughData';

export interface RevenueForecastChartProps extends ChartProps {}

export const RevenueForecastChart: React.FC<RevenueForecastChartProps> = ({
  project,
  analysisFilterState,
  setAnalysisFilterState,
  language,
  palette,
}) => {
  // Adjust shown filters
  useEffect(
    () =>
      setAnalysisFilterState((prev) => ({
        ...prev,
        shownFields: {
          showTagGroupId: true,
          showTagId: true,
          showTargetRankValues: true,
          showRampUpTime: true,
          showAverageOrderValue: true,
          showStartMonthIncrement: true,
          showConversionRate: true,
        },
      })),
    []
  );

  // Data fetching
  const { result, query } = projectsApiService.queries.analysis.useGetTrafficForecast({
    projectId: project.id,
    tagGroupId: analysisFilterState?.tagGroupId,
    tagId: analysisFilterState?.tagId,
    rankValues: analysisFilterState?.targetRankValues ?? [1],
  });

  // Styling
  const theme = useTheme();
  const chartTheme = {
    theme: {
      fontSize: 14,
      color: theme.palette.info.main,
      labels: {
        text: {
          fontSize: 14,
          color: theme.palette.info.main,
        },
      },
    },
  };

  // Render

  if (didQueryTimeout(query)) {
    return <DataTimedOut />;
  }

  if (query?.loading) {
    return <AnalysisChartLoading />;
  }

  if (!result?.lastYearData?.length) {
    return <NotEnoughData />;
  }

  var startMonth = analysisFilterState.startMonthIncrement ?? 6;
  var rampUpTimeMonths = analysisFilterState.rampUpTime ?? 6;
  var targetCtr = result?.targetCtr ?? 0;
  var baselineCtr = result?.baselineCtr ?? 0;
  const averageOrderValue =
    Number(analysisFilterState?.averageOrderValue) > 0
      ? Number(analysisFilterState?.averageOrderValue)
      : 0;
  const conversionRate =
    Number(analysisFilterState?.conversionRate) > 0
      ? Number(analysisFilterState?.conversionRate) / 100
      : 0;

  const sortedHistoricValues = orderBy(result?.lastYearData ?? [], [(x) => x.year, (x) => x.month]);
  const yearArray: number[] = [];
  const data = _.range(12, 36 + startMonth, 1).map((monthIndex) => {
    // Get historic reference value
    const historicIndex = monthIndex % 12;
    const historicValue = sortedHistoricValues[historicIndex];

    // Calculate the new date
    let date = moment
      .utc({ year: historicValue?.year ?? 2022, month: (historicValue?.month ?? 1) - 1 })
      .add(monthIndex - historicIndex, 'months')
      .toDate();

    // Add to the year array if needed
    if (!yearArray.includes(date.getFullYear())) yearArray.push(date.getFullYear());

    // Setup the baseline result
    const result: { baselineTraffic: number; date: Date; pixelTraffic: number } = {
      baselineTraffic: historicValue?.volume * baselineCtr,
      date: date,
      pixelTraffic: undefined,
    };

    if (monthIndex < 12) {
      return result;
    }

    if (monthIndex < 12 + startMonth) {
      return result;
    }

    // Setup the pixel result
    // Calculate ctr
    const currentRampUpMonth = monthIndex - 11 - startMonth;
    const rampUpDegree = Math.min(1, currentRampUpMonth / rampUpTimeMonths);
    const ctr = (1 - rampUpDegree) * baselineCtr + rampUpDegree * targetCtr;
    result.pixelTraffic = historicValue.volume * ctr;

    return result;
  });

  const paletteArray = [
    palette.primaryScale[2],
    palette.tertiaryScale[2],
    palette.tertiaryScale[2],
  ];

  return (
    <div data-screenshot-id="chart-loaded">
      <div className="h-[500px]">
        <ResponsiveLine
          theme={chartTheme.theme}
          data={[
            {
              id: internationalText.chart.revenue_forecast.legend_pixel_forecast[language],
              data: data
                .filter((dp) => dp.pixelTraffic !== undefined)
                .map((dataPoint) => ({
                  x: moment(dataPoint.date).format('yyyy-MM-DD'),
                  y: (dataPoint?.pixelTraffic ?? 0) * conversionRate * averageOrderValue,
                })),
            },
            {
              id: internationalText.chart.revenue_forecast.legend_current_forecast[language],
              data: data.map((dataPoint) => ({
                x: moment(dataPoint.date).format('yyyy-MM-DD'),
                y: dataPoint.baselineTraffic * conversionRate * averageOrderValue,
              })),
            },
          ]}
          legends={[
            {
              anchor: 'top-right',
              direction: 'row',
              justify: false,
              translateX: 0,
              translateY: -30,
              itemsSpacing: 0,
              itemDirection: 'left-to-right',
              itemWidth: 150,
              itemHeight: 20,
              symbolSize: 12,
              symbolBorderColor: 'rgba(0, 0, 0, .5)',
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemBackground: 'rgba(0, 0, 0, .03)',
                    itemOpacity: 1,
                  },
                },
              ],
            },
          ]}
          margin={{ top: 50, right: 50, bottom: 50, left: 70 }}
          xScale={{
            type: 'time',
            format: '%Y-%m-%d',
            useUTC: false,
            precision: 'month',
          }}
          axisLeft={{
            format: '~s',
            tickSize: 5,
            tickPadding: 10,
            tickRotation: 0,
            legend: project.currency ?? 'SEK',
            legendPosition: 'middle',
            legendOffset: -60,
          }}
          axisBottom={{
            tickSize: 5,
            tickPadding: 10,
            tickRotation: 0,
            format: (v: Date) => {
              if (v.getMonth() === 0) return v.getFullYear();
              return 'Q' + (v.getMonth() / 3 + 1);
            },
            tickValues: 'every 3 months',
            legendOffset: -12,
          }}
          markers={yearArray.map((year) => ({
            axis: 'x',
            value: new Date(year, 0, 1),
            lineStyle: { stroke: darken(theme.palette.grey[300], 0), strokeWidth: 3 },
            legendOrientation: 'vertical',
          }))}
          colors={paletteArray}
          enablePoints={true}
          pointBorderWidth={2}
          pointBorderColor={'#fff'}
          pointSize={8}
          lineWidth={3}
          animate={false}
          curve={'monotoneX'}
          isInteractive={false}
        />
      </div>
    </div>
  );
};

export default RevenueForecastChart;
